import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { KENDO_INPUTS, KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaSignIn from '@okta/okta-signin-widget';
import oktaConfigFile from '@app/okta.config';
import { AuthService } from '@services/auth/auth.service';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { environment } from '@env/environment';

const DEFAULT_ORIGINAL_URI = window.location.origin;

@Component({
  selector: 'app-signin',
  imports: [
    KENDO_INPUTS,
    KENDO_TEXTBOX,
    KENDO_LABEL,
    ButtonsModule,
    RouterLink,
    KENDO_LOADER,
  ],
  templateUrl: './signin.component.html',
  styleUrl: './signin.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SignInComponent implements OnInit {
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  authService = inject(AuthService);
  oktaAuth = inject(OKTA_AUTH);
  animationClass: string = '';
  isAuthenticated: boolean = false;
  signIn: any;

  constructor() {
    this.signIn = new OktaSignIn({
      authClient: this.oktaAuth,
      baseUrl: oktaConfigFile.oidc.oktaBaseUrl,
      clientId: oktaConfigFile.oidc.clientId,
      redirectUri: oktaConfigFile.oidc.oktaRedirectUri,
      state: 'fromSignin',
      logo: 'assets/img/MySoftPro-Logo-Okta.png',
      brandName: 'SoftPro',
      colors: {
        brand: '#3791D2',
      },
      i18n: {
        en: {
          'primaryauth.title': 'Sign in to your account',
          'primaryauth.username.placeholder': 'Username/Email',
          'primaryauth.password.placeholder': 'Password',
        },
      },
      authParams: {
        pkce: true,
        issuer: oktaConfigFile.oidc.issuer,
        responseType: 'code',
      },
      useClassicEngine: true,
      //useInteractionCodeFlow: true,
      features: {
        smsRecovery: true,
        callRecovery: true,
        rememberMe: true,
        multiOptionalFactorEnroll: true,
        autoPush: true,
      },
    });
  }

  public async ngOnInit(): Promise<any> {
    const logoutParam = this.route.snapshot.queryParamMap.get('logout');

    if (logoutParam != null) {
      await this.authService.signOut();
    } else {
      this.isAuthenticated = await this.oktaAuth.isAuthenticated();

      if (this.isAuthenticated) {
        // If the user is already authenticated, SSO into the Docs Portal
        this.authService.goToDocsPortal().subscribe((response) => {
          if (
            response != null &&
            response.status === 200 &&
            response.url != null
          ) {
            window.location.href = response.url;
          } else {
            // Handled in interceptor.
          }
        });
        return;
      }
    }

    // When navigating to a protected route, the route path is saved as the `originalUri`
    // If no `originalUri` has been saved, then redirect back to the app root

    // Set where the user is logging in from
    this.oktaAuth.setOriginalUri(
      `${environment.webUrl}${environment.signinReturnUri}`,
    );

    this.signIn
      .showSignInAndRedirect({ el: '#sign-in-widget' })
      .catch(function (error: any) {
        // This function is invoked with errors the widget cannot recover from:
        // Known errors: CONFIG_ERROR, UNSUPPORTED_BROWSER_ERROR
      });

    this.signIn.on('afterRender', async (context: any) => {
      // 0. If context.Controller is not primary-auth, then return
      if (context.controller !== 'primary-auth') {
        return;
      }

      // 1. Get reference to sign in button, username field, and password field
      const signInButton = document.getElementById(
        'okta-signin-submit',
      ) as HTMLButtonElement;
      const signInContainer = signInButton.closest(
        '.o-form-button-bar',
      ) as HTMLDivElement;
      const nextButton = signInButton.cloneNode() as HTMLButtonElement;
      const passwordField = document.getElementById(
        'okta-signin-password',
      ) as HTMLInputElement;
      const passwordContainer = passwordField.closest(
        '.o-form-fieldset',
      ) as HTMLDivElement;
      const usernameField = document.getElementById(
        'okta-signin-username',
      ) as HTMLInputElement;
      const usernameContainer = usernameField.closest(
        '.o-form-fieldset',
      ) as HTMLDivElement;

      // 2. Hide the password field and sign in button

      passwordContainer.style.display = 'none';
      signInButton.style.display = 'none';

      // 3. Stand up new, temporary buttons for 'Next' to emulate OIE widget flow, so that on Click we perform WebFinger
      nextButton.value = 'Next';
      nextButton.onclick = async (evt) => {
        evt.preventDefault();
        const idpName = await this.authService.getAuthDomain(
          usernameField.value,
        );

        if (idpName !== 'softpro') {
          //window.location.href = `${oktaConfigFile.oidc.issuer}/v1/authorize?idp=${oktaConfigFile.oidc.oktaIdp}&client_id=${oktaConfigFile.oidc.clientId}&response_type=code&scope=openid%20email%20profile&state=abc123&redirect_uri=${encodeURIComponent(oktaConfigFile.oidc.oktaRedirectUri)}`;
          // const params = new URLSearchParams({
          //   idp: oktaConfigFile.oidc.oktaIdp,
          //   client_id: oktaConfigFile.oidc.clientId,
          //   response_type: 'code',
          //   scope: oktaConfigFile.oidc.scopes.join(' '),
          //   state: 'fromSignin',
          //   redirect_uri: oktaConfigFile.oidc.oktaRedirectUri,
          // });
          // var tempUrl = `${oktaConfigFile.oidc.issuer}/v1/authorize?${params.toString()}`;
          // console.log('Redirecting to URL -->', tempUrl);
          // window.location.href = `${oktaConfigFile.oidc.issuer}/v1/authorize?${params.toString()}`;
          // return;

          await this.oktaAuth.signInWithRedirect({
            idp: oktaConfigFile.oidc.oktaIdp,
            clientId: oktaConfigFile.oidc.clientId,
            responseType: 'code',
            loginHint: usernameField.value,
            scopes: oktaConfigFile.oidc.scopes,
            originalUri: `${environment.webUrl}${environment.loginReturnUri}`,
            redirectUri: oktaConfigFile.oidc.oktaRedirectUri,
            pkce: true,
            state: 'fromSignin',
          });
          return;
        }

        passwordContainer.style.display = 'block';
        signInButton.style.display = 'block';
        signInContainer.removeChild(nextButton);

        return false;
      };

      signInContainer.appendChild(nextButton);
    });
  }

  public onSignupClick(event: Event) {
    if (!this.isAuthenticated) {
      event.preventDefault();
    }
  }

  // Remove Okta Widget
  ngOnDestroy() {
    this.signIn.remove();
  }
}
